import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';
import type {
  CreateCampaignRequest,
  CreateCampaignResponse,
  GetCampaignsResponse,
  GetCampaignStatsResponse,
  CampaignData,
  CampaignFormData,
  NoRewardCampaignRequest,
  CouponCampaignRequest,
  CashCampaignRequest,
} from '../types/campaign';

/**
 * Create a new campaign
 * Requires authentication (token automatically included)
 * 
 * @param data - Campaign data (can be no reward, coupon, or cash)
 * @returns Promise with campaign creation response
 */
export const createCampaign = async (
  data: CreateCampaignRequest
): Promise<CreateCampaignResponse> => {
  return api.post<CreateCampaignResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CAMPAIGN.CREATE}`,
    data
  );
};

/**
 * Create a campaign with no rewards
 * Requires authentication (token automatically included)
 */
export const createNoRewardCampaign = async (
  data: Omit<NoRewardCampaignRequest, 'reward_type'>
): Promise<CreateCampaignResponse> => {
  return createCampaign({
    ...data,
    reward_type: 'none',
  });
};

/**
 * Create a campaign with coupon rewards
 * Requires authentication (token automatically included)
 */
export const createCouponCampaign = async (
  data: Omit<CouponCampaignRequest, 'reward_type'>
): Promise<CreateCampaignResponse> => {
  return createCampaign({
    ...data,
    reward_type: 'coupon',
  });
};

/**
 * Create a campaign with cash rewards
 * Requires authentication (token automatically included)
 */
export const createCashCampaign = async (
  data: Omit<CashCampaignRequest, 'reward_type'>
): Promise<CreateCampaignResponse> => {
  return createCampaign({
    ...data,
    reward_type: 'cash',
  });
};

/**
 * Helper function to create campaign from form data
 * Automatically selects the right campaign type based on reward_type
 */
export const createCampaignFromForm = async (
  formData: CampaignFormData
): Promise<CreateCampaignResponse> => {
  const baseData = {
    campaign_name: formData.campaignName,
    platform: formData.platform,
    platform_link: formData.platformLink,
  };

  switch (formData.rewardType) {
    case 'none':
      return createNoRewardCampaign(baseData);

    case 'coupon':
      if (
        !formData.couponDiscountPercent ||
        !formData.couponExpiryDate ||
        !formData.couponTerms ||
        !formData.couponPrefix
      ) {
        throw new Error('Coupon fields are required for coupon reward type');
      }
      return createCouponCampaign({
        ...baseData,
        coupon_discount_percent: formData.couponDiscountPercent,
        coupon_expiry_date: formData.couponExpiryDate,
        coupon_terms: formData.couponTerms,
        coupon_prefix: formData.couponPrefix,
      });

    case 'cash':
      if (
        !formData.cashAmount ||
        !formData.cashCurrency ||
        !formData.paymentIntegrator
      ) {
        throw new Error('Cash fields are required for cash reward type');
      }
      return createCashCampaign({
        ...baseData,
        cash_amount: formData.cashAmount,
        cash_currency: formData.cashCurrency,
        payment_integrator: formData.paymentIntegrator,
      });

    default:
      throw new Error(`Unknown reward type: ${formData.rewardType}`);
  }
};

/**
 * Get all campaigns for the authenticated user
 * Requires authentication (token automatically included)
 * 
 * @param limit - Number of campaigns to fetch (default: 10)
 * @param offset - Offset for pagination (default: 0)
 */
export const getCampaigns = async (
  limit: number = 10,
  offset: number = 0
): Promise<GetCampaignsResponse> => {
  return api.get<GetCampaignsResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CAMPAIGN.LIST}/me?limit=${limit}&offset=${offset}`
  );
};

/**
 * Get a single campaign by ID
 * Requires authentication (token automatically included)
 */
export const getCampaignById = async (
  campaignId: string
): Promise<CreateCampaignResponse> => {
  return api.get<CreateCampaignResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CAMPAIGN.GET}/${campaignId}`
  );
};

/**
 * Update a campaign
 * Requires authentication (token automatically included)
 */
export const updateCampaign = async (
  campaignId: string,
  data: Partial<CreateCampaignRequest>
): Promise<CreateCampaignResponse> => {
  return api.put<CreateCampaignResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CAMPAIGN.UPDATE}/${campaignId}`,
    data
  );
};

/**
 * Delete a campaign
 * Requires authentication (token automatically included)
 */
export const deleteCampaign = async (
  campaignId: string
): Promise<{ success: boolean; message?: string }> => {
  return api.delete<{ success: boolean; message?: string }>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CAMPAIGN.DELETE}/${campaignId}`
  );
};

/**
 * Complete/End a campaign
 * Marks a campaign as completed and ends it
 * Requires authentication (token automatically included)
 * 
 * @param campaignId - The ID of the campaign to complete
 * @returns Promise with success/error response
 */
export const completeCampaign = async (
  campaignId: string
): Promise<{ success: boolean; message?: string }> => {
  return api.delete<{ success: boolean; message?: string }>(
    `${API_BASE_URL.CAMPAIGN}${API_ENDPOINTS.CAMPAIGN.COMPLETE}/${campaignId}`
  );
};

/**
 * Get campaign statistics
 * Fetches detailed stats for a specific campaign including contacts, responses, reviews
 * Requires authentication (token automatically included)
 * 
 * @param campaignId - The ID of the campaign to fetch stats for
 * @returns Promise with campaign statistics
 */
export const getCampaignStats = async (
  campaignId: string
): Promise<GetCampaignStatsResponse> => {
  return api.get<GetCampaignStatsResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CONTACT.STATS}/${campaignId}/stats`
  );
};

